import React, { FC } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Button, Tag } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import { CYCLE_EVENTS_CRUD_ACTION_CREATORS } from '../../actions/cycle_events_actions';
import { getStatusLabelFromPipelineFilters, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import CycleEventPipeLineFilters from './CycleEventPipelineFilters';
import {
	cycleEventsRestCrudThunksForBuyer,
	cycleEventsRestCrudThunksForSupplier,
	refreshCycleEventCounts,
} from '../../thunks/cycle_events_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import GeneralStatusDisplay from './GeneralStatusDisplay';
import { ASSET_FIELDS_LABELS_MAP } from '../../utils/assetUtils';
import { withUserType } from '../withUserType';

const ASSET_CYCLE_RUNS_TARGET_COLLECTION = 'AssetCycleRunsIndex';

const AdminCycleEventsIndexPage: FC<any> = ({
	history,
	location,
	updateFilters,
	clearAndUpdateFilters,
	refreshCounts,
	cycleEvents,
	userType,
}): React.ReactElement => {
	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			defaultSortOrder: 'ascend',
			sorter: true,
		},
		{
			title: 'Assets',
			dataIndex: 'assetTypeCount',
			defaultSortOrder: 'ascend',
			sorter: true,
			render: (text, record) => {
				return (
					<span className="bodyregular text-grey-8">
						{nullSafeGetOrElse('assetTypeCount', record, 0)} Asset Groups
					</span>
				);
			},
		},
		{
			title: 'Locations',
			dataIndex: 'locationCount',
			defaultSortOrder: 'ascend',
			sorter: true,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			defaultSortOrder: 'ascend',
			render: (status) => (
				<GeneralStatusDisplay
					statusKey={status}
					statusLabel={getStatusLabelFromPipelineFilters(CycleEventPipeLineFilters, status)}
				/>
			),
			sorter: true,
		},
		{
			title: 'Fields Captured',
			dataIndex: 'captureFields',
			defaultSortOrder: 'ascend',
			render: (text, record) => {
				return nullSafeGetOrElse('captureFields.length', record, 0) > 0
					? (record.captureFields || []).map((field) => (
							<Tag key={field.key}>
								<span className="mr-1 inline-block">
									{field.isRequired ? <span className="text-red-500">*</span> : null}
								</span>
								{ASSET_FIELDS_LABELS_MAP[field.key] || field.key}
							</Tag>
					  ))
					: '--';
			},
		},
	];
	const onRow = (record) => ({
		onClick: () => {
			let searchString = location.search;
			if (searchString[0] === '?') {
				searchString = searchString.slice(1);
			}
			let backUrl = `/${userType}/assetcycleevents/detail/${record.id}/details`;
			if (searchString && searchString.length > 0) {
				backUrl = backUrl.concat(`?backlinkParams=${encodeURIComponent(searchString)}`);
			}
			history.push(backUrl);
		},
	});

	return (
		<PaginatedReduxTableWithHeader
			targetCollectionName={ASSET_CYCLE_RUNS_TARGET_COLLECTION}
			stateSlice={cycleEvents}
			entityCollectionName="cycle_events"
			updateFilters={updateFilters}
			clearAndUpdateFilters={clearAndUpdateFilters}
			handleQueryParams
			refreshCounts={refreshCounts}
			pipeLineFilters={CycleEventPipeLineFilters}
			filterConfig={[]}
			emptyState={
				<EmptyState
					graphic={
						<img
							alt="Currently there are no events!"
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
						/>
					}
					headline={"It's a little lonely in here."}
					body={
						<div style={{ textAlign: 'center' }}>
							<div style={{ maxWidth: 440, marginBottom: 16 }}>
								Looks like your team has not added any cycle runs yet.
							</div>

							<Button
								type="primary"
								onClick={() => history.push(`/${userType}/assetcycleevents/new`)}
							>
								Add your first event
							</Button>
						</div>
					}
				/>
			}
			rightActions={
				<Button type="primary" onClick={() => history.push(`/${userType}/assetcycleevents/new`)}>
					Create Event
				</Button>
			}
			onTableRow={onRow}
			showHeader
			tableColumns={columns}
			initialSorting={{ sort_by: 'createdAt', order: 'desc' }}
			fetchData={
				userType === ROLE_TYPES.SUPPLIER
					? cycleEventsRestCrudThunksForSupplier.read
					: cycleEventsRestCrudThunksForBuyer.read
			}
		/>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	currentUser: state.session.currentUser,
	cycleEvents: state.cycle_events,
	companySettings: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(CYCLE_EVENTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			CYCLE_EVENTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	refreshCounts: (filters) => dispatch(refreshCycleEventCounts(ownProps.userType)(filters)),
});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(AdminCycleEventsIndexPage))
);
